import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Badge, Box, Grid, Typography } from '@mui/material'
import i18n from 'simple-react-i18n'
import { isNil } from 'lodash'
import ConfirmModal from '../../components/modal/ConfirmModal'
import { getDate } from '../../utils/DateUtil'
import { RPQS_STATUSES } from '../constants/RpqsConstant'

const RATIO = 6

const CURRENT_COLOR = '#92d050'
const PASSED_COLOR = '#dce6f2'
const NEXT_COLOR = '#d9d9d9'

const getClipPath = (index) => (
    index === 0
        ? `polygon(0 0, ${100 - RATIO}% 0, 100% 50%, ${100 - RATIO}% 100%, 0% 100%)`
        : `polygon(0 0, ${100 - RATIO}% 0, 100% 50%, ${100 - RATIO}% 100%, 0 100%, ${RATIO}% 50%)`
)

const getStepColor = (code, status) => {
    if (code === status) return CURRENT_COLOR
    return code < status ? PASSED_COLOR : NEXT_COLOR
}

const RPQSBreadCrumb = ({
    status = 0,
    dates = [],
    badges,
    canGoToNext = false,
    onGoToNext = () => {},
}) => {
    const [openConfirm, setOpenConfirm] = useState(false)
    const nextStatus = RPQS_STATUSES[status + 1]

    return (
        <>
            <Grid container alignItems='flex-start'>
                {RPQS_STATUSES.map(({ code, name }) => {
                    const isClickable = canGoToNext && code === status
                    const badge = badges?.[code]
                    return (
                        <Grid
                            key={code}
                            size={2}
                            textAlign='center'
                            onClick={() => isClickable && setOpenConfirm(true)}
                            sx={{ cursor: isClickable ? 'pointer' : 'default' }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    minHeight: 40,
                                    '@keyframes rpqsStatusPulse': {
                                        '0%, 100%': { transform: 'scale(1)' },
                                        '50%': { transform: 'scale(1.06)' },
                                    },
                                    animation: isClickable ? 'rpqsStatusPulse 1.4s ease-in-out infinite' : 'none',
                                }}
                            >
                                <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#000', clipPath: getClipPath(code) }} />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: '1px 1px 1px 2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.5rem',
                                        bgcolor: getStepColor(code, status),
                                        color: '#000',
                                        clipPath: getClipPath(code),
                                    }}
                                >
                                    <Typography variant='body2' fontWeight='bold'>
                                        {i18n[name]}
                                    </Typography>
                                </Box>
                                {!!badge && (
                                    <Badge
                                        badgeContent={badge}
                                        color='primary'
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 20,
                                            '& .MuiBadge-badge': {
                                                height: 30,
                                                width: 30,
                                                borderRadius: '50%',
                                                border: '2px solid black',
                                                fontSize: '1rem',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography variant='body2' fontWeight='bold' color='text.secondary' sx={{ display: 'block', paddingTop: 0.5 }}>
                                {isNil(dates[code]) ? '' : getDate(dates[code])}
                            </Typography>
                        </Grid>
                    )
                })}
            </Grid>
            <ConfirmModal
                isOpen={openConfirm}
                title={nextStatus ? `${i18n.rpqsConfirmNextStatus} « ${i18n[nextStatus.name]} » ?` : ''}
                onValidate={() => {
                    setOpenConfirm(false)
                    onGoToNext()
                }}
                onClose={() => setOpenConfirm(false)}
            />
        </>
    )
}

RPQSBreadCrumb.propTypes = {
    status: PropTypes.number,
    dates: PropTypes.arrayOf(PropTypes.number),
    badges: PropTypes.arrayOf(PropTypes.number),
    canGoToNext: PropTypes.bool,
    onGoToNext: PropTypes.func,
}

export default RPQSBreadCrumb
